import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Head component that reliably injects meta tags and JSON-LD schemas.
 * Works around react-helmet-async's script injection issues in CRA.
 */
const SEOHead = ({ title, description, canonical, ogTitle, ogDescription, ogType, ogImage, twitterCard, schemas = [] }) => {
  const location = useLocation();

  useEffect(() => {
    // Title
    if (title) document.title = title;

    // Meta description
    const setMeta = (name, content, prop) => {
      if (!content) return;
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (prop) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:type', ogType || 'website', true);
    setMeta('og:url', canonical, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', 'Buzz Craft Maven', true);
    setMeta('og:locale', 'en_IN', true);
    setMeta('twitter:card', twitterCard || 'summary_large_image');
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);
    if (ogImage) setMeta('twitter:image', ogImage);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    // JSON-LD Schemas - clean up previous page-specific ones, inject new
    document.querySelectorAll('script[data-seo-page]').forEach(s => s.remove());
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-page', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup page-specific schemas on unmount
      document.querySelectorAll('script[data-seo-page]').forEach(s => s.remove());
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogType, ogImage, twitterCard, schemas, location.pathname]);

  return null;
};

export default SEOHead;
